import { API_ENDPOINTS } from '../config/api';
import { apiClient } from './api/apiClient';

/**
 * 운영 프로세스 자식 행 (depth 2)
 *
 * 상위 카테고리 행 ('인프라 구축', '장비 설치' 등) 아래 펼쳐서 입력하는
 * 세부 작업. 순서는 별도 필드 없이 배열 인덱스+1 ('1', '2', ...) 로
 * derived 렌더링한다.
 */
export interface OperationProcessChildRow {
  id: string;
  checked: boolean;
  name: string;
  content: string;
  date: string;
  remark: string;
  /** 작업자 (담당자 이름). 기존 행에는 없을 수 있어 optional. */
  worker?: string;
  /** 준비물 · 체크리스트 항목 목록. 기존 행에는 없을 수 있어 optional. */
  checklist?: OperationChecklistItem[];
}

/**
 * 준비물 · 체크리스트 항목 한 개 (업무절차 워크플로 뷰에서 사용).
 *
 * 행/자식 행에 붙는 체크 가능한 준비 항목. 기존 데이터에는 없을 수 있어
 * 모든 소비처에서 optional 로 다룬다.
 */
export interface OperationChecklistItem {
  id: string;
  text: string;
  done: boolean;
}

/**
 * 관련 문서 링크 한 개.
 *
 * 운영 프로세스 행에 첨부하는 참고 문서 URL. 행마다 여러 개 가질 수 있다.
 */
export interface OperationDocLink {
  /** 표시 이름(선택). 비우면 URL 을 그대로 표시한다. */
  label?: string;
  /** 문서 URL. */
  url: string;
}

/**
 * 운영 프로세스 한 행
 *
 * - `docLinks`: 관련 문서 링크 목록 (여러 개 가능)
 * - `worker`: 작업자 (편집 가능 텍스트)
 * - `children`: 자식 행 목록 (펼치기로 노출되는 depth 2 작업). optional.
 *
 * 모두 기존 행에 없을 수 있어 optional — 마이그레이션 없이 점진적 도입.
 */
export interface OperationProcessRow {
  id: string;
  category: string;
  checked: boolean;
  name: string;
  date: string;
  content: string;
  /** 관련 문서 링크 목록 (여러 개 가능). 기존 행에는 없을 수 있어 optional. */
  docLinks?: OperationDocLink[];
  /** 작업자 (담당자 이름). 기존 행에는 없을 수 있어 optional. */
  worker?: string;
  remark: string;
  /**
   * 준비사항 메모 (자유 텍스트). 과거 '작업 내용 및 준비사항' 컬럼의 값으로,
   * UI 에서 컬럼이 제거된 뒤에도 저장 데이터에는 남아 있다 — 워크플로 뷰가
   * '준비물 · 체크리스트' 아래 메모로 다시 노출한다.
   */
  preparation?: string;
  /** 준비물 · 체크리스트 항목 목록. 기존 행에는 없을 수 있어 optional. */
  checklist?: OperationChecklistItem[];
  /** 자식 작업 목록 (depth 2). 기존 행에는 없을 수 있어 optional. */
  children?: OperationProcessChildRow[];
}

/**
 * 운영 프로세스 현장 정보(헤더)
 */
export interface OperationProcessSiteInfo {
  siteName: string;
  location: string;
  manager: string;
  startDate: string;
  endDate: string;
  customer: string;
  contact: string;
  siteCode: string;
  remark: string;
}

/**
 * 저장되는 데이터 전체 형태
 */
export interface OperationProcessData {
  siteInfo?: OperationProcessSiteInfo;
  rows: OperationProcessRow[];
}

interface ApiResponse {
  success: boolean;
  message: string;
  data?: OperationProcessData | null;
}

/**
 * GET 응답에서 OperationProcessData 추출. null/undefined → 빈 데이터.
 */
function unwrap(data: OperationProcessData | null | undefined): OperationProcessData {
  if (!data) return { rows: [] };
  return {
    siteInfo: data.siteInfo,
    rows: Array.isArray(data.rows) ? data.rows : [],
  };
}

export class OperationProcessService {
  /** 현장별 운영 프로세스 조회 */
  static async getBySite(siteId: number): Promise<OperationProcessData> {
    const res = await apiClient.get<ApiResponse>(
      API_ENDPOINTS.SITE_MANAGEMENT.OPERATION_PROCESS.GET_BY_SITE(siteId)
    );
    if (!res.data.success) {
      throw new Error(res.data.message || '운영 프로세스 조회 실패');
    }
    return unwrap(res.data.data);
  }

  /** 현장별 운영 프로세스 저장 */
  static async updateBySite(siteId: number, data: OperationProcessData): Promise<void> {
    const res = await apiClient.put<ApiResponse>(
      API_ENDPOINTS.SITE_MANAGEMENT.OPERATION_PROCESS.UPDATE_BY_SITE(siteId),
      data
    );
    if (!res.data.success) {
      throw new Error(res.data.message || '운영 프로세스 저장 실패');
    }
  }

  /** 글로벌 템플릿 조회 */
  static async getTemplate(): Promise<OperationProcessData> {
    const res = await apiClient.get<ApiResponse>(
      API_ENDPOINTS.SITE_MANAGEMENT.OPERATION_PROCESS.GET_TEMPLATE
    );
    if (!res.data.success) {
      throw new Error(res.data.message || '템플릿 조회 실패');
    }
    return unwrap(res.data.data);
  }

  /** 글로벌 템플릿 저장 */
  static async updateTemplate(data: OperationProcessData): Promise<void> {
    const res = await apiClient.put<ApiResponse>(
      API_ENDPOINTS.SITE_MANAGEMENT.OPERATION_PROCESS.UPDATE_TEMPLATE,
      data
    );
    if (!res.data.success) {
      throw new Error(res.data.message || '템플릿 저장 실패');
    }
  }
}

/**
 * 운영 프로세스 진행률 계산 — 부모 행 + 자식 행을 모두 합쳐
 * checked 된 개수 / 전체 개수 비율 산출.
 */
export interface OperationProgress {
  done: number;
  total: number;
  pct: number;
}

export function computeOperationProgress(
  data: OperationProcessData | null | undefined
): OperationProgress {
  if (!data || !Array.isArray(data.rows) || data.rows.length === 0) {
    return { done: 0, total: 0, pct: 0 };
  }
  let done = 0;
  let total = 0;
  for (const r of data.rows) {
    total += 1;
    if (r.checked) done += 1;
    const children = Array.isArray(r.children) ? r.children : [];
    for (const c of children) {
      total += 1;
      if (c.checked) done += 1;
    }
  }
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  return { done, total, pct };
}

/**
 * 다음으로 진행할 작업의 이름을 반환.
 *
 * 우선순위:
 * 1) checked === false 인 부모 행이 있으면 그 부모 (children 중 첫 미체크 자식
 *    이름이 있으면 그 자식 이름까지 합쳐 "부모 ▶ 자식" 형태).
 * 2) 부모는 모두 checked 인데 children 중 미체크가 있으면 그 자식.
 * 3) 전부 완료면 null.
 *
 * 이름이 비어 있는 행은 placeholder 로 카테고리 + index 표시.
 */
export function findNextOperationStep(
  data: OperationProcessData | null | undefined
): { label: string; categoryLabel?: string } | null {
  if (!data || !Array.isArray(data.rows) || data.rows.length === 0) return null;

  for (let i = 0; i < data.rows.length; i += 1) {
    const r = data.rows[i];
    const children = Array.isArray(r.children) ? r.children : [];
    if (!r.checked) {
      const nextChild = children.find((c) => !c.checked);
      const parentName = (r.name || '').trim() || `${r.category || '항목'} #${i + 1}`;
      const childName = nextChild
        ? (nextChild.name || '').trim() || '하위 작업'
        : null;
      const label = childName ? `${parentName} ▶ ${childName}` : parentName;
      return { label, categoryLabel: r.category };
    }
    const nextChild = children.find((c) => !c.checked);
    if (nextChild) {
      const parentName = (r.name || '').trim() || `${r.category || '항목'} #${i + 1}`;
      const childName = (nextChild.name || '').trim() || '하위 작업';
      return { label: `${parentName} ▶ ${childName}`, categoryLabel: r.category };
    }
  }
  return null;
}
